import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Plus, Search, Upload, ExternalLink, Trash2, CheckCircle2,
  AlertCircle, DollarSign, X, Copy,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";

const fmtAUD = (n) =>
  n == null ? "—" : new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 }).format(n);

const STATUS_BADGE = {
  paid: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  unpaid: "bg-white/10 text-white/60 border-white/20",
  overdue: "bg-red-500/15 text-red-400 border-red-500/30",
  waived: "bg-white/5 text-white/40 border-white/10",
};

export default function AdminTolls() {
  const [tolls, setTolls] = useState([]);
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, sRes] = await Promise.all([
        api.get("/admin/tolls", { params: { status, q: q || undefined } }),
        api.get("/admin/tolls/summary"),
      ]);
      setTolls(tRes.data);
      setSummary(sRes.data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [status, q]);

  useEffect(() => { load(); }, [load]);

  const markPaid = async (id) => {
    try {
      await api.post(`/admin/tolls/${id}/mark-paid`);
      toast.success("Marked paid");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const removeToll = async (id) => {
    if (!window.confirm("Delete this toll?")) return;
    try {
      await api.delete(`/admin/tolls/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const copyStripeLink = async (id) => {
    try {
      const { data } = await api.post(`/admin/tolls/${id}/stripe-link`);
      await navigator.clipboard.writeText(data.checkout_url);
      toast.success("Stripe checkout link copied");
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-tolls-page" className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 bg-[#080808] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
          <Logo />
          <Link
            to="/admin"
            data-testid="admin-tolls-back"
            className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-[#D4AF37] tracking-wide"
          ><ArrowLeft size={14} /> Back to Dashboard</Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-8 py-10">
        <div className="label-eyebrow mb-3">RTO · Toll Management</div>
        <h1 className="font-display text-4xl md:text-5xl mb-6" style={{ fontWeight: 300 }}>
          Toll <span className="italic text-[#D4AF37]">Console</span>
        </h1>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <SummaryCard label="TOTAL" primary={summary?.total_count ?? 0} />
          <SummaryCard label="OUTSTANDING" primary={fmtAUD(summary?.outstanding_amount ?? 0)} tone="warn" />
          <SummaryCard label="PAID" primary={summary?.by_status?.paid?.count ?? 0} />
          <SummaryCard label="ADMIN FEE" primary={fmtAUD(summary?.admin_fee ?? 5.5)} />
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <button
            onClick={() => setShowAdd(true)}
            data-testid="admin-tolls-add"
            className="inline-flex items-center gap-1.5 bg-[#D4AF37] text-[#050505] font-semibold rounded px-4 py-2 text-xs tracking-widest hover:bg-[#E5C158]"
          ><Plus size={13} /> ADD TOLL</button>
          <button
            onClick={() => setShowImport(true)}
            data-testid="admin-tolls-import"
            className="inline-flex items-center gap-1.5 border border-white/10 text-white rounded px-4 py-2 text-xs tracking-widest hover:border-[#D4AF37]/40"
          ><Upload size={13} /> IMPORT CSV</button>

          <div className="ml-auto flex items-center gap-2">
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                data-testid="admin-tolls-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search rego, ref, customer…"
                className="bg-[#0a0a0a] border border-white/10 rounded pl-8 pr-3 py-1.5 text-xs w-64 focus:outline-none focus:border-[#D4AF37]/50"
              />
            </div>
            <select
              data-testid="admin-tolls-status-filter"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="bg-[#0a0a0a] border border-white/10 rounded px-3 py-1.5 text-xs focus:outline-none"
            >
              {["all", "unpaid", "overdue", "paid", "waived"].map((s) =>
                <option key={s} value={s}>{s.toUpperCase()}</option>
              )}
            </select>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-white/40 text-sm">Loading…</div>
        ) : tolls.length === 0 ? (
          <div className="border border-white/10 border-dashed rounded-lg p-10 text-center text-white/40 text-sm">
            No tolls yet — add one manually or import a CSV.
          </div>
        ) : (
          <div className="border border-white/10 rounded-lg overflow-hidden overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="bg-white/5 text-[10px] tracking-widest text-white/50">
                  <th className="text-left px-3 py-3">DATE</th>
                  <th className="text-left px-3 py-3">CUSTOMER</th>
                  <th className="text-left px-3 py-3">REGO</th>
                  <th className="text-left px-3 py-3">PROVIDER</th>
                  <th className="text-left px-3 py-3">LOCATION</th>
                  <th className="text-right px-3 py-3">TOLL</th>
                  <th className="text-right px-3 py-3">FEE</th>
                  <th className="text-right px-3 py-3">TOTAL</th>
                  <th className="text-left px-3 py-3">STATUS</th>
                  <th className="text-right px-3 py-3">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {tolls.map((t) => (
                  <tr key={t.id} className="border-t border-white/10">
                    <td className="px-3 py-3">{t.event_date}</td>
                    <td className="px-3 py-3">
                      <div className="text-white">{t.customer_name}</div>
                      <div className="text-xs text-white/40">{t.customer_email}</div>
                    </td>
                    <td className="px-3 py-3 text-white/70">{t.vehicle_rego}</td>
                    <td className="px-3 py-3 text-white/70">{t.provider}</td>
                    <td className="px-3 py-3 text-white/60 max-w-xs truncate">{t.location || "—"}</td>
                    <td className="px-3 py-3 text-right">{fmtAUD(t.toll_amount)}</td>
                    <td className="px-3 py-3 text-right text-white/60">{fmtAUD(t.admin_fee)}</td>
                    <td className="px-3 py-3 text-right text-white">{fmtAUD(t.total_amount)}</td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] tracking-widest uppercase ${STATUS_BADGE[t.status] || ""}`}>
                        {t.status === "paid" && <CheckCircle2 size={11} />}
                        {t.status === "overdue" && <AlertCircle size={11} />}
                        {t.status}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="inline-flex items-center gap-3">
                        {t.status !== "paid" && (
                          <>
                            <button
                              onClick={() => copyStripeLink(t.id)}
                              data-testid={`admin-toll-link-${t.id}`}
                              title="Copy Stripe checkout link"
                              className="text-white/60 hover:text-[#D4AF37]"
                            ><Copy size={13} /></button>
                            <button
                              onClick={() => markPaid(t.id)}
                              data-testid={`admin-toll-mark-paid-${t.id}`}
                              title="Mark paid"
                              className="text-white/60 hover:text-emerald-400"
                            ><CheckCircle2 size={13} /></button>
                          </>
                        )}
                        <button
                          onClick={() => removeToll(t.id)}
                          data-testid={`admin-toll-delete-${t.id}`}
                          title="Delete"
                          className="text-white/60 hover:text-red-400"
                        ><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {showAdd && <AddTollModal onClose={() => setShowAdd(false)} onCreated={load} />}
      {showImport && <ImportCsvModal onClose={() => setShowImport(false)} onDone={load} />}
    </div>
  );
}

function SummaryCard({ label, primary, tone }) {
  return (
    <div className="border border-white/10 rounded-lg p-4 bg-[#0a0a0a]">
      <div className="text-[10px] tracking-widest text-white/50 mb-1">{label}</div>
      <div className={`font-display text-2xl ${tone === "warn" ? "text-red-300" : "text-white"}`} style={{ fontWeight: 300 }}>
        {primary}
      </div>
    </div>
  );
}

function AddTollModal({ onClose, onCreated }) {
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({
    vehicle_id: "", customer_id: "", provider: "Linkt",
    reference: "", event_date: new Date().toISOString().slice(0, 10),
    location: "", toll_amount: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const overlayRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [fRes, cRes] = await Promise.all([
          api.get("/admin/rto/fleet"),
          api.get("/admin/rto/customers"),
        ]);
        setVehicles(fRes.data);
        setCustomers(cRes.data);
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id || !form.toll_amount) return toast.error("Vehicle and toll amount are required");
    setSaving(true);
    try {
      await api.post("/admin/tolls", {
        ...form,
        toll_amount: Number(form.toll_amount),
        customer_id: form.customer_id || undefined,
      });
      toast.success("Toll added");
      onCreated();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
    >
      <div data-testid="admin-toll-add-modal" className="bg-[#0a0a0a] border border-white/10 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <div className="text-[#D4AF37] text-[10px] tracking-widest">ADD TOLL</div>
            <div className="font-display text-xl" style={{ fontWeight: 400 }}>New toll charge</div>
          </div>
          <button onClick={onClose} data-testid="admin-toll-add-close" className="text-white/60 hover:text-white"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <ModalField label="VEHICLE">
            <select
              data-testid="admin-toll-add-vehicle"
              required
              value={form.vehicle_id}
              onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })}
              className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm"
            >
              <option value="">— Select a vehicle —</option>
              {vehicles.map((v) =>
                <option key={v.id} value={v.id}>{v.year} {v.make} {v.model} · {v.rego}</option>
              )}
            </select>
          </ModalField>
          <ModalField label="CUSTOMER (optional — auto-inferred from active lease)">
            <select
              data-testid="admin-toll-add-customer"
              value={form.customer_id}
              onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
              className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm"
            >
              <option value="">— Auto-detect —</option>
              {customers.map((c) =>
                <option key={c.id} value={c.id}>{c.full_name} · {c.email}</option>
              )}
            </select>
          </ModalField>
          <div className="grid grid-cols-2 gap-3">
            <ModalField label="PROVIDER">
              <select
                data-testid="admin-toll-add-provider"
                value={form.provider}
                onChange={(e) => setForm({ ...form, provider: e.target.value })}
                className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm"
              >
                {["Linkt", "EastLink", "Transurban", "Other"].map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </ModalField>
            <ModalField label="EVENT DATE">
              <input
                data-testid="admin-toll-add-date"
                type="date" required
                value={form.event_date}
                onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm"
              />
            </ModalField>
          </div>
          <ModalField label="LOCATION / GANTRY">
            <input
              data-testid="admin-toll-add-location"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="e.g. M2 Motorway"
              className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm"
            />
          </ModalField>
          <div className="grid grid-cols-2 gap-3">
            <ModalField label="TOLL AMOUNT (AUD)">
              <input
                data-testid="admin-toll-add-amount"
                type="number" step="0.01" min="0" required
                value={form.toll_amount}
                onChange={(e) => setForm({ ...form, toll_amount: e.target.value })}
                placeholder="5.62"
                className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm"
              />
            </ModalField>
            <ModalField label="PROVIDER REFERENCE">
              <input
                data-testid="admin-toll-add-reference"
                value={form.reference}
                onChange={(e) => setForm({ ...form, reference: e.target.value })}
                placeholder="TR1234567"
                className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm"
              />
            </ModalField>
          </div>
          <div className="text-xs text-white/50 border border-white/10 rounded p-3 flex items-center gap-2">
            <DollarSign size={12} className="text-[#D4AF37]" />
            A flat <span className="text-white">$5.50</span> admin fee is added automatically. Customer sees toll + fee as one total.
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
            <button type="button" onClick={onClose} className="px-4 py-2 text-xs tracking-widest text-white/70 hover:text-white">CANCEL</button>
            <button
              type="submit"
              data-testid="admin-toll-add-submit"
              disabled={saving}
              className="bg-[#D4AF37] text-[#050505] font-semibold rounded px-5 py-2 text-xs tracking-widest hover:bg-[#E5C158] disabled:opacity-50"
            >{saving ? "SAVING…" : "ADD TOLL"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ImportCsvModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [provider, setProvider] = useState("Linkt");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/tolls/import/csv?provider=${provider}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
    >
      <div data-testid="admin-toll-import-modal" className="bg-[#0a0a0a] border border-white/10 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <div className="text-[#D4AF37] text-[10px] tracking-widest">IMPORT CSV</div>
            <div className="font-display text-xl" style={{ fontWeight: 400 }}>Bulk import tolls</div>
          </div>
          <button onClick={onClose} data-testid="admin-toll-import-close" className="text-white/60 hover:text-white"><X size={18} /></button>
        </div>
        <form onSubmit={upload} className="p-5 space-y-4">
          <div className="text-xs text-white/60 leading-relaxed border border-white/10 rounded p-3 bg-white/[0.02]">
            <div className="text-white mb-1 tracking-wide text-[10px]">CSV FORMAT</div>
            Required columns: <code>rego</code>, <code>event_date</code>, <code>toll_amount</code><br />
            Optional: <code>reference</code>, <code>location</code>, <code>notes</code><br />
            Date accepts <code>YYYY-MM-DD</code> or <code>DD/MM/YYYY</code>.
          </div>
          <ModalField label="PROVIDER">
            <select
              data-testid="admin-toll-import-provider"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm"
            >
              {["Linkt", "EastLink", "Transurban", "Other"].map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </ModalField>
          <ModalField label="CSV FILE">
            <input
              data-testid="admin-toll-import-file"
              type="file" accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-xs text-white/70"
            />
          </ModalField>
          {result && (
            <div className="border border-white/10 rounded p-3 text-xs space-y-1">
              <div className="text-emerald-400">Imported: {result.imported_count}</div>
              {result.error_count > 0 && (
                <>
                  <div className="text-red-400">Errors: {result.error_count}</div>
                  <ul className="text-white/60 mt-1 space-y-0.5 max-h-32 overflow-y-auto">
                    {result.errors.map((e, i) => <li key={i}>· {e}</li>)}
                  </ul>
                </>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
            <button type="button" onClick={onClose} className="px-4 py-2 text-xs tracking-widest text-white/70 hover:text-white">
              {result ? "CLOSE" : "CANCEL"}
            </button>
            {!result && (
              <button
                type="submit"
                data-testid="admin-toll-import-submit"
                disabled={uploading || !file}
                className="bg-[#D4AF37] text-[#050505] font-semibold rounded px-5 py-2 text-xs tracking-widest hover:bg-[#E5C158] disabled:opacity-50"
              >{uploading ? "UPLOADING…" : "UPLOAD & IMPORT"}</button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

function ModalField({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-widest text-white/50 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
